/**
 * UI Tags parser and formatter.
 *
 * Implements the Elrond chat backend marker grammar:
 *   [[ui:<type>:<id>(:<json_payload>)?]]
 *
 * See: UI Tags — Frontend Integration Spec (registry v2)
 */

/** Known spec types. Widened to string so unknown future types are caught and silently dropped. */
export type UITagType = 'link' | 'rich' | 'action' | (string & {});

export type MetricFormat =
    | 'usd'
    | 'percent'
    | 'percent_signed'
    | 'count'
    | 'score_10'
    | 'x_multiplier'
    | 'days'
    | 'currency_compact'
    | 'raw_number';

export type MetricTone = 'positive' | 'neutral' | 'negative' | 'warning';

export interface HighlightedMetric {
    key: string;
    label: string;
    value: number;
    format: MetricFormat;
    tone: MetricTone;
}

export interface ParsedMarker {
    type: UITagType;
    id: string;
    payload: Record<string, unknown> | null;
    raw: string;
    start: number;
    end: number;
}

export type TextSegment =
    | { kind: 'text'; value: string }
    | { kind: 'marker'; marker: ParsedMarker };

/**
 * Regex matching [[ui:type:id]] and [[ui:type:id:{"payload"}]]
 * Group 1: type — any lowercase word (spec knows link|rich|action; others are caught and silently dropped per §11)
 * Group 2: id
 * Group 3: raw JSON payload object string (optional)
 *
 * The lookahead (?=\]\]) anchors the JSON object close to the ]] closer
 * and allows nested {} in the payload (e.g. highlighted_metrics arrays).
 */
const MARKER_REGEX =
    /\[\[ui:(link|rich|action):([a-z0-9._-]+)(?::(\{[^\n]*?\}(?=\]\])))?\]\]/g;

export function parseMarkers(text: string): ParsedMarker[] {
    const out: ParsedMarker[] = [];
    for (const m of text.matchAll(MARKER_REGEX)) {
        const [raw, type, id, payloadRaw] = m;
        let payload: Record<string, unknown> | null = null;
        if (payloadRaw) {
            try {
                const parsed: unknown = JSON.parse(payloadRaw);
                if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    payload = parsed as Record<string, unknown>;
                } else {
                    continue; // malformed payload — drop marker
                }
            } catch {
                continue; // JSON parse failure — drop marker
            }
        }
        out.push({
            type: type as UITagType,
            id,
            payload,
            raw,
            start: m.index ?? 0,
            end: (m.index ?? 0) + raw.length,
        });
    }
    return out;
}

export function segmentText(text: string): TextSegment[] {
    const markers = parseMarkers(text);
    const segs: TextSegment[] = [];
    let cursor = 0;
    for (const mk of markers) {
        if (mk.start > cursor) {
            segs.push({ kind: 'text', value: text.slice(cursor, mk.start) });
        }
        segs.push({ kind: 'marker', marker: mk });
        cursor = mk.end;
    }
    if (cursor < text.length) {
        segs.push({ kind: 'text', value: text.slice(cursor) });
    }
    return segs;
}

/**
 * Format a raw numeric value according to the spec §8 contract.
 */
export function formatMetricValue(value: number, format: MetricFormat): string {
    switch (format) {
        case 'usd':
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(value);

        case 'percent':
            return `${(value * 100).toFixed(1)}%`;

        case 'percent_signed': {
            const pct = value * 100;
            return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
        }

        case 'count':
            return new Intl.NumberFormat('en-US').format(Math.round(value));

        case 'score_10':
            return `${value.toFixed(1)} / 10`;

        case 'x_multiplier':
            return `${value.toFixed(1)}x`;

        case 'days':
            return `${Math.round(value)} days`;

        case 'currency_compact':
            if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
            if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
            return `$${value.toFixed(0)}`;

        case 'raw_number':
        default:
            return new Intl.NumberFormat('en-US').format(value);
    }
}

/** Validate that a string is a known MetricFormat value. */
export function isMetricFormat(v: unknown): v is MetricFormat {
    const FORMATS: MetricFormat[] = [
        'usd', 'percent', 'percent_signed', 'count',
        'score_10', 'x_multiplier', 'days', 'currency_compact', 'raw_number',
    ];
    return typeof v === 'string' && (FORMATS as string[]).includes(v);
}

/** Validate that a string is a known MetricTone value. */
export function isMetricTone(v: unknown): v is MetricTone {
    return v === 'positive' || v === 'neutral' || v === 'negative' || v === 'warning';
}

/**
 * Parse and validate a highlighted_metrics entry.
 * Returns null if the entry is structurally invalid (defensive spec §11).
 */
export function parseHighlightedMetric(raw: unknown): HighlightedMetric | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const m = raw as Record<string, unknown>;
    if (
        typeof m.key !== 'string' ||
        typeof m.label !== 'string' ||
        typeof m.value !== 'number' ||
        !isFinite(m.value as number) ||
        !isMetricFormat(m.format) ||
        !isMetricTone(m.tone)
    ) {
        return null;
    }
    return {
        key: m.key,
        label: m.label,
        value: m.value as number,
        format: m.format,
        tone: m.tone,
    };
}
